import React, { useCallback, useState, useEffect, useRef } from "react";
import { hooks } from "botframework-webchat-component";
import { FaPaperPlane } from "react-icons/fa";
import MessageItem from "./MessageItem";
import WelcomeTiles from "./WelcomeTiles";
import SuggestionList from "./SuggestionList";
import { RiUserSmileLine } from "react-icons/ri";
import SpeechToText from "./SpeechToText";
import { sanitizeString } from "../../utils/sanitize";

const { useActivities, useSendMessage, useSendEvent } = hooks;

// INTERNALLY USED COMPONENT(S)

const convertArabicToEnglishDigits = (str) => {
  const arabicDigits = "٠١٢٣٤٥٦٧٨٩"; // Arabic digits
  const englishDigits = "0123456789"; // English digits
  return str
    .split("")
    .map((char) => {
      const index = arabicDigits.indexOf(char);
      return index !== -1 ? englishDigits[index] : char;
    })
    .join("");
};



const formatDateLabel = (date,isArabic) => {
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  const activityDate = new Date(date);

  let timeString = isArabic
    ? activityDate.toLocaleTimeString("ar-EG", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true, // Ensures AM/PM is used in Arabic
      })
    : activityDate.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true, // Ensures AM/PM is used in English
      });

  timeString = convertArabicToEnglishDigits(timeString);

  if (isArabic) {
    timeString = timeString.replace("ص", "صباحاً").replace("م", "مساءً");
  }

  if (activityDate.toDateString() === today.toDateString()) {
    return isArabic ? `اليوم  ${timeString}` : `Today at ${timeString}`;
  }

  if (activityDate.toDateString() === yesterday.toDateString()) {
    return isArabic ? `أمس في ${timeString}` : `Yesterday at ${timeString}`;
  }

  return isArabic
    ? activityDate.toLocaleString("ar-EG", { hour12: true }) // Full Arabic date
    : activityDate.toLocaleString(); // Default locale
};

const DateDivider = ({ date,isArabic }) => (
  <div className="flex items-center my-4">
    <hr className="flex-grow border-gray-300" />
    <span className="mx-2 text-gray-500 text-sm">
      {formatDateLabel(date,isArabic)}
    </span>{" "}
    <hr className="flex-grow border-gray-300" />
  </div>
);


const ChatContent = ({ isArabic }) => {

  //HOOKS INTIALIZATION
  const [activities] = useActivities();
  const sendMessage = useSendMessage();
  const sendEvent = useSendEvent();
  const [sendBoxValue, setSendBoxValue] = useState("");
  const messagesEndRef = useRef(null);
  const [streamingText, setStreamingText] = useState("");
  const processedActivityIds = useRef(new Set()); // To track processed activities
  const prevActivitiesRef = useRef([]);
  const [suggestions, setSuggestions] = useState([]); // State for suggestions
  const [isThinking, setIsThinking] = useState(false);
  const [isSurveyButtonDisabled, setSurveyButtonDisabled] = useState(false); // State for button disable
  const [isRecording, setIsRecording] = useState(false);
  const [surveyMapping, setSurveyMapping]= useState({})
  const [responseFeedbackMapping, setResponseFeedbackMapping] = useState({}); // State for suggestions
  const [arabicMessages, setArabicMessages] = useState({}); // State for suggestions
  const [userLastMessageArabic, setUserLastMessageArabic] = useState(isArabic);
  const processedSuggestionIdsRef = useRef(new Set());
  const [autoScrollDisabled, setAutoScrollDisabled] = useState(false);
  const disableTimeoutRef = useRef(null);
  const lastScrollTopRef = useRef(0);

  //ESSENTIAL CALLBACK FUNCTIONS

  const updateSuggestions = useCallback((parsedValue) => {
    setSuggestions(parsedValue.queries);
    setSurveyButtonDisabled(false);
  }, []);

  const handleChange = useCallback(
    ({ target: { value } }) => setSendBoxValue(value),
    []
  );

  const createUserActivity = (text) => ({
    type: "message",
    channelData: { role: "user" },
    text: sanitizeString(text),
  });

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      const sanitizedValue = sanitizeString(sendBoxValue.trim());
      if (sanitizedValue) {
        try {
          const userActivity = createUserActivity(sanitizedValue);
          await sendMessage(sanitizedValue, undefined, {
            channelData: userActivity.channelData,
          });
          setIsThinking(true);
          setSendBoxValue("");
          setSuggestions([]);
          setSurveyButtonDisabled(true);
        } catch (error) {
          console.error("Error sending message:", error);
        }
      }
    },
    [sendBoxValue, sendMessage]
  );

  const onDemandSurvey = useCallback(() => {
    if (isSurveyButtonDisabled) return;
    setSurveyButtonDisabled(true);
    setSuggestions([]);
    sendEvent("survey");
  }, [isSurveyButtonDisabled, sendEvent]);

  const clearState = () => {
    setSendBoxValue("");
    setSuggestions([]);
  };

  const onSuggestionClick = useCallback(
    (suggestion) => {
      setIsThinking(true);
      setSurveyButtonDisabled(true);
      sendMessage(sanitizeString(suggestion));
      clearState();
    },
    [sendMessage]
  );

  //SCROLLING FUNCTION
  const handleUserScroll = (event) => {
    const { scrollTop, scrollHeight, clientHeight } = event.target;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;

    if (!isAtBottom && scrollTop < lastScrollTopRef.current) {
      setAutoScrollDisabled(true);

      if (disableTimeoutRef.current) {
        clearTimeout(disableTimeoutRef.current);
      }

      disableTimeoutRef.current = setTimeout(() => {
        setAutoScrollDisabled(false);
      }, 3000); // 4 seconds
    }

    lastScrollTopRef.current = scrollTop;
  };


// USE EFFECT HOOKS USED FOR LOGIC
  useEffect(() => {
    const cleanedActivities = activities.filter(
      (activity) => activity.type !== "typing"
    );
    console.log("cleaned", cleanedActivities);

    if (activities.length >= 2) {
      const lastActivity = activities[activities.length - 1];
      const penultimateActivity = activities[activities.length - 2];
      if (lastActivity?.from?.name?.toLowerCase()==='bot' && lastActivity?.channelData?.streamType?.toLowerCase() ==='final' ){
        setIsThinking(false)
      }

      // Handle survey button state based on activities
      if (
        lastActivity.type === "message" &&
        penultimateActivity.type === "typing"
      ) {
        setSurveyButtonDisabled(false);
      }

      if (
        lastActivity.type === "event" &&
        lastActivity.name === "submitFeedback"
      ) {
        setSurveyButtonDisabled(false);
      }

      if (
        lastActivity.type === "event" &&
        lastActivity.name === "surveyData"
      ) {
        setSurveyButtonDisabled(true);
      }
    }

    activities.forEach((activity) => {
      // Translate message text
      if (activity.type === "message" && activity.text) {
        const translate = activity.text.split("|||");
        activity.text = translate.length > 1 && userLastMessageArabic
          ? translate[1]
          : translate[0];
      }

      // Handle streaming text
      if (
        activity.type === "typing" &&
        activity.channelData?.streamType === "streaming" &&
        !processedActivityIds.current.has(activity.id)
      ) {
        setStreamingText((prev) =>
          prev ? `${prev}${activity.text}` : activity.text
        );
        if (streamingText !== "") setIsThinking(false);
        processedActivityIds.current.add(activity.id);
      } else if (activity.type === "message") {
        setStreamingText("");
        processedActivityIds.current.clear();
      }

      // Handle suggestions
      if (
        activity.type === "event" &&
        activity.name === "suggestions" &&
        !processedSuggestionIdsRef.current.has(activity.id)
      ) {
        try {
          let parsedValue = activity.value;
          if (typeof parsedValue === "string") {
            parsedValue = JSON.parse(parsedValue);
          }

          if (
            typeof parsedValue === "object" &&
            parsedValue !== null &&
            Array.isArray(parsedValue.queries)
          ) {
            updateSuggestions(parsedValue, activity.id);
            processedSuggestionIdsRef.current.add(activity.id);
          } else {
            console.error("Invalid suggestions format:", parsedValue);
          }
        } catch (error) {
          console.error("Failed to parse suggestions:", error);
        }
      }

      // Handle Arabic messages mapping
      if (
        activities.length &&
        activities[activities.length - 1].type === "message"
      ) {
        const lastActivity = activities[activities.length - 1];
        setArabicMessages((prev) => ({
          ...prev,
          [lastActivity.id]: userLastMessageArabic,
        }));
      }
    });
  }, [activities, updateSuggestions, userLastMessageArabic,streamingText]);


  useEffect(() => {
    const prevActivities = prevActivitiesRef.current;
    const newActivities = activities.filter(
      (activity) => !prevActivities.find((a) => a.id === activity.id)
    );

    const hasNewMessage = newActivities.some(
      (activity) => activity.type === "message" || activity.type === "typing"
    );

    if (hasNewMessage && !autoScrollDisabled) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }

    prevActivitiesRef.current = activities;

    const lastMessage = activities
      .filter((activity) => activity.type === "message")
      .pop();

    if (lastMessage) {
      const messageText = lastMessage.text;
      const arabicRegex = /[\u0600-\u06FF]/;
      const isArabicMessage = arabicRegex.test(messageText);
      const isNumeric = /^-?\d+(\.\d+)?$/.test(messageText);

      if (!messageText || messageText === "") {
        return;
      } else if (isNumeric) {
        return;
      } else if (isArabicMessage && userLastMessageArabic !== true) {
        setUserLastMessageArabic(true);
      } else if (!isArabicMessage && userLastMessageArabic !== false) {
        setUserLastMessageArabic(false);
      }
    }
  }, [activities, autoScrollDisabled, userLastMessageArabic]);
  useEffect(() => {
    setSurveyMapping((prevSurveyMapping) => {
      const newSurveyMapping = { ...prevSurveyMapping };

      activities.forEach((activity) => {
        if (
          activity.type === "event" &&
          activity.name === "submitFeedback"
        ) {
          newSurveyMapping[activity.value.id] = activity.value.data;
        }
      });

      return newSurveyMapping;
    });

    setResponseFeedbackMapping(() => {
      const newFeedbackMapping = {};

      activities.forEach((activity) => {
        if (
          activity.type === "event" &&
          (activity.name === "ThumbsUp" || activity.name === "ThumbsDown")
        ) {
          const feedbackValue = activity.name === "ThumbsUp" ? 1 : -1;
          const messageId = activity.value;

          newFeedbackMapping[messageId] =
            newFeedbackMapping[messageId] === feedbackValue
              ? 0
              : feedbackValue;
        }
      });

      return newFeedbackMapping;
    });
  }, [activities]);

//STYLING CONSTANTS

const MOBILE_WIDTH = 768;
const COLOR_DISABLED = "#94c3ba";
const COLOR_ENABLED = "#007560";
const BG_COLOR_INPUT = "#f6f6f6";
const BG_COLOR_BUTTON_DISABLED = "#d3d3d3";
const OUTLINE_COLOR_RECORDING = "1px solid #007560";
const DROP_SHADOW_ACTIVE = "drop-shadow(0 0 10px rgba(0, 117, 96, 0.8))";
const DROP_SHADOW_INACTIVE = "drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))";
const isMobile = window.innerWidth <= MOBILE_WIDTH;
const inputHeight = isMobile ? "35px" : "40px";
const inputPadding = isMobile ? "6px" : "8px";
const fontSize = isMobile ? "0.9rem" : "1.125rem";
const flexDirection = isArabic ? "row-reverse" : "row";
const direction = isArabic ? "rtl" : "ltr";
const placeholderText = isArabic ? "كيف يمكنني مساعدتك؟" : "How can I help you?";

// Common styles for input and buttons USED FOR DYNAMIC RENDERING
const inputStyle = {
  backgroundColor: BG_COLOR_INPUT,
  flex: 1,
  height: inputHeight,
  padding: inputPadding,
  borderRadius: "12px",
  outline: "none",
  textAlign: isArabic ? "right" : "left",
  width: "100%",
  boxSizing: "border-box",
};

const submitButtonStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "40px",
  height: "40px",
  color: "white",
  cursor: !sendBoxValue || streamingText || isThinking ? "not-allowed" : "pointer",
  backgroundColor: !sendBoxValue || streamingText || isThinking ? COLOR_DISABLED : COLOR_ENABLED,
  borderRadius: "30px",
  outline: "none",
};

const buttonStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: isMobile ? "6vh" : "5vh",
  height: isMobile ? "6vh" : "5vh",
  marginLeft: userLastMessageArabic ? "0.5vh" : "",
  marginRight: userLastMessageArabic ? "" : "0.5vh",
  borderRadius: "12px",
  backgroundColor: isSurveyButtonDisabled ? BG_COLOR_BUTTON_DISABLED : "white",
  cursor: isSurveyButtonDisabled ? "not-allowed" : "pointer",
};

// Style for outer padding based on screen size
const outerPaddingStyle = isMobile
  ? { padding: "0" }
  : { paddingLeft: "4rem", paddingRight: "4rem" };

// Render the UI
return (
  <div className="bg-white" style={outerPaddingStyle}>
    <div className="w-full bg-white" style={{ height: '100vh' }}>
      <div className="flex flex-col p-4 max-w-5xl mx-auto" style={{ flexGrow: 1, fontSize, height: '100vh' }}>
        <div className="flex-1 overflow-y-auto p-2 md:p-4" onScroll={handleUserScroll}>
          <ul className="space-y-4 relative">
            {activities.filter(({ type }) => type === "message").length <= 1 && (
              <WelcomeTiles setIsThinking={setIsThinking} isArabic={isArabic} loaded={activities.length === 0} />
            )}

            {activities.filter(({ type }) => type === "message").map((activity, index) => {
              const feedback = responseFeedbackMapping[activity.id] || 0;
              const suggestionsandsources = activity.suggestedActions?.actions || [];
              const sources = suggestionsandsources.filter(item => item.type === "openUrl");
              const suggestedActions = suggestionsandsources.filter(item => item.type !== "openUrl");
              const surveyData = surveyMapping[activity.id];
              const language = arabicMessages[activity.id];

              const commonMessageItemProps = {
                isArabic: language,
                activity,
                id: activity.id,
                responseGood: feedback,
                surveyData,
                suggestedActionlist: suggestedActions,
                disableSend: (!!streamingText || isThinking || activities.length === 0)
              };

              return index === 0 ? (
                <React.Fragment key={`fragment-${activity.id}`}>
                  <DateDivider date={activity.timestamp} isArabic={isArabic} />
                  <MessageItem {...commonMessageItemProps} isLastMessage={true} />
                </React.Fragment>
              ) : (
                <MessageItem
                  {...commonMessageItemProps}
                  locked={!!streamingText || isThinking}
                  key={activity.id || `message-${index}`}
                  sources={sources}
                  onSuggestionClick={onSuggestionClick}
                />
              );
            })}

            {isThinking && (
              <MessageItem
                isArabic={userLastMessageArabic}
                key="thinking"
                activity={{
                  id: "thinking",
                  text: userLastMessageArabic ? "البوت يفكر..." : "Bot is thinking...",
                  type: "typing",
                  from: { role: "agent" },
                  channelData: { streamType: "streaming" },
                  timestamp: new Date().toISOString(),
                }}
              />
            )}

            {streamingText && (
              <MessageItem
                isArabic={userLastMessageArabic}
                key="streaming"
                activity={{
                  id: "streaming",
                  text: streamingText,
                  type: "typing",
                  from: { role: "agent" },
                  timestamp: new Date().toISOString(),
                  channelData: { streamType: "streaming" },
                }}
              />
            )}

            <div ref={messagesEndRef} />
          </ul>
        </div>

        {suggestions.length > 0 && (
          <SuggestionList suggestions={suggestions} onSuggestionClick={onSuggestionClick} />
        )}

        <div className="mt-4" style={{ marginBottom: isMobile ? "0" : "1rem" }}>
          <form onSubmit={handleSubmit} className="flex items-center" style={{ flex: "1" }}>
            {!isArabic && (
              <button
                onClick={onDemandSurvey}
                type="button"
                className={`bg-gray-200 rounded-full ${isMobile ? '' : 'ml-2'}`}
                style={buttonStyle}
              >
                <RiUserSmileLine className="text-4xl" style={{ color: COLOR_ENABLED }} />
              </button>
            )}

            <div
              style={{
                display: "flex",
                alignItems: "center",
                backgroundColor: BG_COLOR_INPUT,
                border: "1px solid #D1D5DB",
                borderRadius: "16px",
                padding: "8px",
                flex: "1",
                marginBottom: "4px",
                outline: isRecording ? OUTLINE_COLOR_RECORDING : "none",
                filter: isRecording ? DROP_SHADOW_ACTIVE : DROP_SHADOW_INACTIVE,
                transition: "filter 0.2s ease-in-out, outline 0.2s ease-in-out",
                flexDirection,
              }}
            >
              <input
                autoFocus
                onChange={handleChange}
                type="text"
                value={sendBoxValue}
                style={inputStyle}
                dir={direction}
                placeholder={placeholderText}
              />
              <div>
                <SpeechToText
                  text={sendBoxValue}
                  setText={setSendBoxValue}
                  recording={isRecording}
                  setRecording={setIsRecording}
                />
              </div>
              <button
                type="submit"
                disabled={!sendBoxValue || !!streamingText || isThinking || activities.length === 0}
                style={submitButtonStyle}
              >
                <FaPaperPlane style={{ fontSize: "1.125rem" }} />
              </button>
            </div>

            {isArabic && (
              <button
                onClick={onDemandSurvey}
                type="button"
                className={`bg-gray-200 rounded-full ${isMobile ? '' : 'ml-2'}`}
                style={buttonStyle}
              >
                <RiUserSmileLine className="text-4xl" style={{ color: COLOR_ENABLED }} />
              </button>
            )}
          </form>
        </div>

        <div
          className={`flex items-center ${!isMobile ? 'mx-auto' : ''}`}
          style={{
            color: "gray",
            direction,
            textAlign: "center",
            justifyContent: "center",
            alignItems: "center",
            paddingBottom: isMobile ? "" : "12px",
            fontSize: isMobile ? "13px" : "15px",
          }}
        >
          {isMobile
            ? isArabic
              ? "رماس يعتمد على الذكاء الاصطناعي التوليدي التوليدي، يرجى التحقق من الإجابات."
              : "Rammas uses GenAI; verify answers as needed."
            : isArabic
            ? (
              <>
                رماس، المدعوم بالذكاء الاصطناعي التوليدي، يعتمد على البيانات المتوفرة ويجب التحقق منها عند الضرورة.<br />
                يرجى تقييم الإجابات للمساعدة في تحسين رماس.
              </>
            )
            : (
              <>
                Rammas, powered by Generative AI, is based on available data and should be verified further when necessary.<br />
                Please rate the responses to help improve Rammas.
              </>
            )}
        </div>
      </div>
    </div>
  </div>
);

};

export default ChatContent;
